import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { setErrorSnacks } from '../utils/error';
import { useAppSnackbar } from './useAppSnackbar';

interface OpenOptions {
  fileName?: string;
  fetchDocument?: () => Promise<{ data: Blob; headers: { 'content-type': string } }>;
  silentError?: boolean;
}

export function useDocumentPreview() {
  const [isLoading, setIsLoading] = useState(false);
  const [iframeSrc, setIframeSrc] = useState('');
  const [contentType, setContentType] = useState('');
  const [fileName, setFileName] = useState('');
  const dataUrl = useRef<string | undefined>(undefined);
  const cachedContentType = useRef<string | undefined>(undefined);
  const cachedDocUrl = useRef<string | undefined>(undefined);
  const abortRef = useRef<AbortController | undefined>(undefined);
  const { enqueueErrorSnackbar } = useAppSnackbar();

  useEffect(() => {
    return () => {
      if (dataUrl.current) {
        window.URL.revokeObjectURL(dataUrl.current);
      }
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!iframeSrc) return;
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIframeSrc('');
        setContentType('');
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [iframeSrc]);

  const open = useCallback(
    async (documentUrl: string, options?: OpenOptions) => {
      const { fileName: name, fetchDocument, silentError } = options ?? {};

      if (dataUrl.current && cachedContentType.current && cachedDocUrl.current === documentUrl) {
        setContentType(cachedContentType.current);
        setIframeSrc(dataUrl.current);
        setFileName(name || 'document');
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (dataUrl.current) {
        window.URL.revokeObjectURL(dataUrl.current);
        dataUrl.current = undefined;
        cachedContentType.current = undefined;
        cachedDocUrl.current = undefined;
      }

      setIframeSrc('');
      setIsLoading(true);
      try {
        const response = fetchDocument
          ? await fetchDocument()
          : await axios.get(documentUrl, { responseType: 'blob', signal: controller.signal });

        if (controller.signal.aborted) return;

        const type = String(response.headers['content-type'] ?? '');
        const blob = new Blob([response.data], { type });
        dataUrl.current = window.URL.createObjectURL(blob);
        cachedContentType.current = type;
        cachedDocUrl.current = documentUrl;
        setContentType(type);
        setIframeSrc(dataUrl.current);
        setFileName(name || 'document');
      } catch (err) {
        if (axios.isCancel(err)) return;
        if (!silentError) {
          setErrorSnacks(err, enqueueErrorSnackbar);
        }
      }
      setIsLoading(false);
    },
    [enqueueErrorSnackbar],
  );

  const close = useCallback(() => {
    setIframeSrc('');
    setContentType('');
  }, []);

  const download = useCallback(() => {
    if (!dataUrl.current) return;
    const link = document.createElement('a');
    link.href = dataUrl.current;
    link.download = fileName || 'document';
    link.click();
  }, [fileName]);

  return { open, close, download, isLoading, iframeSrc, contentType, fileName };
}
